import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { Subject, takeUntil } from 'rxjs';
import { AppExceptionModel } from '../../../models/app-exception.model';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../../models/member.school.model';
import { MembershipStatus } from '../../../models/membership.status';
import { SchoolGradeService } from '../../../services/grade.school.service';
import { UtilsService } from '../../../services/utils.service';
import * as gradeActions from '../../../state/actions/grade.school.actions';
import * as memberActions from '../../../state/actions/member.school.actions';
import { AppState } from '../../../state/appstate';
import * as memberSelector from '../../../state/selectors/member.school.selector';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';

@Component({
  selector: 'app-dlg-assign-faculty',
  standalone: true,
  imports: [BtnActionComponent],
  templateUrl: './dlg-assign-faculty.component.html',
  styleUrl: './dlg-assign-faculty.component.css',
})
export class DlgAssignFacultyComponent implements OnInit, OnDestroy {
  store: Store<AppState> = inject(Store<AppState>);
  private actions$: Actions = inject(Actions);
  gradeService: SchoolGradeService = inject(SchoolGradeService);
  utilsService = inject(UtilsService);
  logger: NGXLogger = inject(NGXLogger);
  readonly dialogRef = inject(MatDialogRef<DlgAssignFacultyComponent>);
  private dialog: MatDialog = inject(MatDialog);
  readonly data = inject<any>(MAT_DIALOG_DATA);

  title: string = 'Assign Faculty';
  description: string = `Following are the teachers available to assign to grade '${this.data.grade.gradeName}'`;
  action: string = 'Assign';
  faculty: SchoolMember[] | undefined;
  selectedOption: string;

  working: boolean = false;

  exit$: Subject<void> = new Subject();

  constructor() {
    this.selectedOption = this.data.selectedOption ?? 'unassigned';

    this.store
      .select(
        memberSelector.getGroupMembersByType({
          groupId: this.data.groupId!,
          memberType: [SchoolMemberType.teacher],
          status: [MembershipStatus.active],
        }),
      )
      .pipe(takeUntil(this.exit$))
      .subscribe((faculty) => {
        this.faculty = faculty;
        if (faculty?.length === 0) {
          this.store.dispatch(
            memberActions.getMembers({
              source: 'DlgAssignFacultyComponent :: constructor',
              groupId: this.data.groupId!,
              getMemberType: [SchoolMemberType.teacher],
            }),
          );
        }
      });
  }

  ngOnInit(): void {
    this.actions$
      .pipe(ofType(gradeActions.reassignGradeSuccess), takeUntil(this.exit$))
      .subscribe(() => {
        this.working = false;
        this.dialogRef.close();
      });

    this.actions$
      .pipe(ofType(gradeActions.reassignGradeFailure), takeUntil(this.exit$))
      .subscribe(() => {
        this.working = false;
        this.utilsService.onShowAlert(
          {
            title: 'Error',
            message: 'Failed to reassign faculty. Please try again.',
          } as AppExceptionModel,
          false,
        );
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onChangeOption(event: any): void {
    this.selectedOption = event.target.value;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  submit() {
    this.working = true;

    const currTeacher = this.faculty?.find(
      (t) => t.memberId === this.data.grade.teacherId,
    );

    const newTeacher = this.faculty?.find(
      (t) => t.memberId === this.selectedOption,
    );

    if (currTeacher?.memberId === newTeacher?.memberId) {
      this.utilsService.onShowAlert({
        title: 'Error',
        message: 'Cannot reassign to the same teacher.',
      } as AppExceptionModel);
      return;
    }

    this.store.dispatch(
      gradeActions.reassignGrade({
        groupId: this.data.groupId!,
        gradeId: this.data.grade.id,
        gradeYear: this.data.grade.gradeYear,
        teacherId: newTeacher?.memberId ?? undefined,
      }),
    );
  }
}
